/**
 * Central cookie/consent state.
 *
 * DSGVO: download tracking and the pseudonymous visitor marker are only
 * activated after explicit consent to the "analytics" category. Withdrawing
 * consent removes the marker immediately.
 */

export type ConsentCategory = "necessary" | "functional" | "analytics" | "marketing";

export interface CookiePreferences {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CONSENT_KEY = "cookie-consent";
export const PREFERENCES_KEY = "cookie-preferences";
export const CONSENT_DATE_KEY = "cookie-consent-date";
export const CONSENT_EVENT = "eap-consent-changed";
export const OPEN_SETTINGS_EVENT = "eap-open-cookie-settings";
/** Permanent opt-out of download tracking / attribution (overrides consent). */
export const TRACKING_OPTOUT_KEY = "eap-tracking-optout";
export const VISITOR_ID_KEY = "eap-visitor-id";

export const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export const hasDecided = (): boolean => {
  try {
    return !!localStorage.getItem(CONSENT_KEY);
  } catch {
    return false;
  }
};

export const getPreferences = (): CookiePreferences => {
  try {
    if (!localStorage.getItem(CONSENT_KEY)) return DEFAULT_PREFERENCES;
    const raw = localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<CookiePreferences>;
    return {
      necessary: true,
      functional: !!parsed.functional,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const hasConsent = (category: ConsentCategory): boolean => {
  if (category === "necessary") return true;
  if (category === "analytics" && isTrackingOptedOut()) return false;
  return getPreferences()[category];
};

/** Download tracking + pseudonymous visitor ID are gated on this. */
export const hasAnalyticsConsent = (): boolean => hasConsent("analytics");

/** Permanent opt-out: no download logging, no visitor marker — ever. */
export const isTrackingOptedOut = (): boolean => {
  try {
    return localStorage.getItem(TRACKING_OPTOUT_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Activates/deactivates the permanent download-tracking opt-out.
 * When activated, the pseudonymous marker is removed locally AND detached
 * from all stored attribution data on the server (immediate effect).
 */
export const setTrackingOptOut = async (enabled: boolean): Promise<void> => {
  let visitorId: string | null = null;
  try {
    visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (enabled) {
      localStorage.setItem(TRACKING_OPTOUT_KEY, "1");
      const prefs = getPreferences();
      if (localStorage.getItem(CONSENT_KEY)) {
        localStorage.setItem(
          PREFERENCES_KEY,
          JSON.stringify({ ...prefs, analytics: false, necessary: true }),
        );
      }
    } else {
      localStorage.removeItem(TRACKING_OPTOUT_KEY);
    }
  } catch {
    /* storage unavailable */
  }

  if (enabled) {
    clearPseudonymisation();
    if (visitorId) {
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tracking-optout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visitor_id: visitorId }),
        });
      } catch {
        /* best effort — local tracking is off regardless */
      }
    }
  }

  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: getPreferences() }));
};

export const getConsentDate = (): string | null => {
  try {
    return localStorage.getItem(CONSENT_DATE_KEY);
  } catch {
    return null;
  }
};

export const setPreferences = (prefs: Omit<CookiePreferences, "necessary">) => {
  const next: CookiePreferences = { necessary: true, ...prefs };
  try {
    localStorage.setItem(CONSENT_KEY, "configured");
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(next));
    localStorage.setItem(CONSENT_DATE_KEY, new Date().toISOString());
  } catch {
    /* storage unavailable — consent simply stays off */
  }
  if (!next.analytics) clearPseudonymisation();
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }));
  return next;
};

/** Removes the pseudonymous visitor marker (right to withdraw). */
export const clearPseudonymisation = () => {
  try {
    localStorage.removeItem("eap-visitor-id");
  } catch {
    /* ignore */
  }
};

/** Full withdrawal: clears the decision so the banner is shown again. */
export const revokeConsent = () => {
  try {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(PREFERENCES_KEY);
    localStorage.removeItem(CONSENT_DATE_KEY);
  } catch {
    /* ignore */
  }
  clearPseudonymisation();
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: DEFAULT_PREFERENCES }));
};

export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
};
